import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Button,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  ArrowBack,
  VideoLibrary,
  Quiz,
  Assignment,
  School,
  CheckCircle,
  Schedule,
} from '@mui/icons-material';

interface ActivityTimelineProps {}

const ActivityTimeline: React.FC<ActivityTimelineProps> = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [studentFilter, setStudentFilter] = useState('all');
  const [activityType, setActivityType] = useState('all');
  const [dateRange, setDateRange] = useState('week');

  // Mock data
  const activities = [
    {
      id: 1,
      timestamp: '2025-01-15 14:30',
      student: 'John Doe',
      activityType: 'quiz',
      activity: 'Completed Quiz: Linear Equations',
      score: 85,
      duration: 15,
    },
    {
      id: 2,
      timestamp: '2025-01-15 13:15',
      student: 'Jane Smith',
      activityType: 'video',
      activity: 'Watched Video: Quadratic Functions',
      duration: 12,
    },
    {
      id: 3,
      timestamp: '2025-01-15 12:00',
      student: 'Bob Johnson',
      activityType: 'assignment',
      activity: 'Submitted Assignment: Polynomials',
      score: 92,
    },
    {
      id: 4,
      timestamp: '2025-01-15 11:30',
      student: 'John Doe',
      activityType: 'topic',
      activity: 'Completed Topic: Linear Equations',
      progress: 100,
    },
    {
      id: 5,
      timestamp: '2025-01-15 10:00',
      student: 'Alice Brown',
      activityType: 'quiz',
      activity: 'Attempted Quiz: Trigonometry',
      score: 68,
      attempts: 2,
    },
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'quiz':
        return <Quiz />;
      case 'video':
        return <VideoLibrary />;
      case 'assignment':
        return <Assignment />;
      case 'topic':
        return <CheckCircle />;
      default:
        return <School />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'quiz':
        return 'primary';
      case 'video':
        return 'info';
      case 'assignment':
        return 'success';
      case 'topic':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate(`/admin/courses/${courseId}/dashboard`)}
            >
              Back to Dashboard
            </Button>
            <Typography variant="h4" component="h1">
              Activity Timeline
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Student</InputLabel>
            <Select
              value={studentFilter}
              label="Student"
              onChange={(e) => setStudentFilter(e.target.value)}
            >
              <MenuItem value="all">All Students</MenuItem>
              <MenuItem value="1">John Doe</MenuItem>
              <MenuItem value="2">Jane Smith</MenuItem>
              <MenuItem value="3">Bob Johnson</MenuItem>
              <MenuItem value="4">Alice Brown</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Activity Type</InputLabel>
            <Select
              value={activityType}
              label="Activity Type"
              onChange={(e) => setActivityType(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="quiz">Quiz</MenuItem>
              <MenuItem value="video">Video</MenuItem>
              <MenuItem value="assignment">Assignment</MenuItem>
              <MenuItem value="topic">Topic Completion</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Activities
              </Typography>
              <Typography variant="h4">1,245</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active Students
              </Typography>
              <Typography variant="h4">189</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Avg Activities/Day
              </Typography>
              <Typography variant="h4">89</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Peak Hour
              </Typography>
              <Typography variant="h4">2:00 PM</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Timeline */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Recent Activities
        </Typography>
        <Timeline>
          {activities.map((activity, index) => (
            <TimelineItem key={activity.id}>
              <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                {activity.timestamp}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineConnector />
                <TimelineDot color={getActivityColor(activity.activityType)}>
                  {getActivityIcon(activity.activityType)}
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent sx={{ py: '12px', px: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {activity.student}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activity.activity}
                  </Typography>
                  <Box display="flex" gap={1} mt={1}>
                    {activity.score && (
                      <Chip label={`Score: ${activity.score}%`} size="small" color="primary" />
                    )}
                    {activity.duration && (
                      <Chip label={`${activity.duration} min`} size="small" variant="outlined" />
                    )}
                    {activity.attempts && (
                      <Chip label={`Attempt ${activity.attempts}`} size="small" variant="outlined" />
                    )}
                    {activity.progress && (
                      <Chip label={`${activity.progress}% Complete`} size="small" color="success" />
                    )}
                  </Box>
                </Box>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </Paper>
    </Container>
  );
};

export default ActivityTimeline;

